import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col, Modal, Spin } from 'antd';
import AppPageHeader from '@/components/_common/AppPageHeader';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import ManufacturersReportModal from '@/components/Operational/Inventory/Manufacturers/ManufacturersReportModal';
import {
  useManufacturers,
  useManufacturerStatus,
} from '@/core/domain/operational/Manufacturers/Manufacturers.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { ColumnsType } from 'antd/lib/table';
import {
  ListManufacturersQuery,
  Manufacturer,
} from '@/core/domain/operational/Manufacturers/Manufacturers.types';
import { InfiniteTable, StatusIcon } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import { useDebounce, useDisclosure } from '@/hooks';
import { COLUMNS_WIDTH } from '@/utils/table';
import ActiveExtraFilter from '@/components/_common/ExtraFilters/ActiveExtraFilter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

const ManufacturersPage = ({ route }: PageProps) => {
  const intl = useIntl();

  const [modalReportOpened, modalReportActions] = useDisclosure();

  const [queryParams, setQueryParams] = useState<ListManufacturersQuery>({
    order: 'ASC',
    rows: 30,
    active: true,
    ...convertParams<ListManufacturersQuery>({
      name: 'string',
      active: 'boolean',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, hasNextPage, fetchNextPage, isLoading } = useManufacturers(
    queryDebounce,
    hasPermissions([Authorities.REGISTER_MANUFACTER_VIEW]),
  );

  usePersistSearchParams(queryDebounce);

  const items = flatInfiniteData(data);

  const manufacturerStatus = useManufacturerStatus(!queryParams.active);

  function handleStatus(manufacturer: Manufacturer) {
    Modal.confirm({
      title: intl.formatMessage({
        id: `manufacturers.modal.status.${
          queryParams.active ? 'disable' : 'enable'
        }.title`,
      }),
      content: `${intl.formatMessage({
        id: `manufacturers.modal.status.${
          queryParams.active ? 'disable' : 'enable'
        }.description`,
      })} "${manufacturer.name}"?`,
      onOk: () => manufacturerStatus.mutateAsync(manufacturer.id),
      okText: intl.formatMessage({
        id: `commons.list.action.button.${
          queryParams.active ? 'disable' : 'enable'
        }`,
      }),
    });
  }

  const columns: ColumnsType<Manufacturer> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: intl.formatMessage({
        id: 'manufacturers.list.header.description',
      }),
      ellipsis: true,
      width: COLUMNS_WIDTH.default,
    },

    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      ellipsis: true,
      width: COLUMNS_WIDTH.small,
      fixed: 'right',
      align: 'right',
      render: (_, row) =>
        manufacturerStatus.isLoading &&
        manufacturerStatus.variables === row.id ? (
          <Spin size="small" />
        ) : (
          <StatusIcon
            active={queryParams.active ?? true}
            onClick={() => handleStatus(row)}
            disabled={
              !hasPermissions([Authorities.REGISTER_MANUFACTER_ACTIVE_INACTIVE])
            }
          />
        ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: 'manufacturers.page.title' })} />

      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        persistSearchParams={false}
        extra={
          <Button
            disabled={
              !hasPermissions([Authorities.REGISTER_MANUFACTER_REPORTS])
            }
            block
            onClick={modalReportActions.open}
            type="primary"
          >
            <FormattedMessage id="manufacturers.page.button.generateReport" />
          </Button>
        }
      >
        <AppPageHeader
          searchValue={queryParams.name}
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, name: value }))
          }
          extraFilters={
            <Col xl={6} md={12} xs={24}>
              <ActiveExtraFilter
                value={queryParams?.active}
                onChange={(value) => {
                  setQueryParams((prev) => ({
                    ...prev,
                    active: value === 'true' ? true : false,
                  }));
                }}
              />
            </Col>
          }
        />

        <InfiniteTable
          columns={columns}
          loading={isLoading}
          dataSource={items}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
        />

        <ManufacturersReportModal
          open={modalReportOpened}
          onCancel={modalReportActions.close}
        />
      </DefaultRouteTabs>
    </>
  );
};

export default ManufacturersPage;
